/**
 * Profile FX: converts multi-currency balances into the user’s `displayCurrency` using
 * automatic rates from `CurrencyConversionContext`. **Not** used for portfolio quote totals
 * when `BalanceDisplay.keepOriginalCurrency` is set (those stay on the market quote axis).
 */
import type { CurrencyBalances } from "types/Balance";
import { DEFAULT_DISPLAY_CURRENCY, FX_RATE_MAX, FX_RATE_MIN, defaultConversionRatesForDisplay } from "shared/money/currencyDefaults";

/**
 * Merges user overrides on top of app defaults (legacy persisted rates; auto mode ignores manual values).
 */
export function mergeConversionRates(
    userRates: Record<string, number> | undefined,
    displayCurrency: string = DEFAULT_DISPLAY_CURRENCY,
): Record<string, number> {
    return { ...defaultConversionRatesForDisplay(displayCurrency), ...userRates };
}

/**
 * Converts a map of balances by currency into one amount in `displayCurrency`.
 * @see currencyDefaults.ts for the meaning of each `conversionRates[c]`.
 */
function effectiveFxDivisor(rate: number | undefined): number {
    if (rate == null || !Number.isFinite(rate) || rate <= 0) return 1;
    if (rate < FX_RATE_MIN) return FX_RATE_MIN;
    if (rate > FX_RATE_MAX) return FX_RATE_MAX;
    return rate;
}

export function convertBalanceToDisplayCurrency(
    userBalanceByCurrency: CurrencyBalances,
    displayCurrency: string,
    conversionRates: Record<string, number>
): number {
    let total = 0;
    for (const [currency, amount] of Object.entries(userBalanceByCurrency)) {
        if (currency === displayCurrency) {
            total += amount;
        } else {
            total += amount / effectiveFxDivisor(conversionRates[currency]);
        }
    }
    return total;
}

/** Single-currency slice of {@link convertBalanceToDisplayCurrency}. */
export function toDisplayAmount(
    amount: number,
    fromCurrency: string,
    displayCurrency: string,
    conversionRates: Record<string, number>
): number {
    return convertBalanceToDisplayCurrency({ [fromCurrency]: amount }, displayCurrency, conversionRates);
}
